// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { broadcastResponseToMainFrame } from "@azure/msal-browser/redirect-bridge";

/*
 * Relays a popup/silent sign-in response back to the window that started it.
 * The flights app's redirectUri is the bare origin "/", which this app serves,
 * so its auth response lands here instead of in the flights window;
 * broadcastResponseToMainFrame() posts it back over a BroadcastChannel. Imported
 * first in index.js so it strips the response from the URL before this app's own
 * MSAL reads the hash.
 *
 * Gated to popup/silent responses only: a redirect response is this app's own
 * sign-in and must be left for handleRedirectPromise() — relaying it would race
 * that and can loop. We identify the flow by decoding MSAL's state parameter.
 */
function b64UrlDecode(value) {
  let b64 = value.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) {
    b64 += "=";
  }
  // eslint-disable-next-line no-restricted-globals
  return decodeURIComponent(escape(window.atob(b64)));
}

try {
  const hash = (window.location.hash || "").replace(/^#/, "");
  const search = (window.location.search || "").replace(/^\?/, "");
  const params = new URLSearchParams(hash || search);
  const hasResponse = params.has("code") || params.has("error");
  const state = params.get("state") || "";

  let isMsalState = false;
  let interactionType = "";
  if (state) {
    const core = decodeURIComponent(state).split("|")[0];
    try {
      const parsed = JSON.parse(b64UrlDecode(core));
      isMsalState = !!(parsed && parsed.id && parsed.meta);
      interactionType =
        (parsed && parsed.meta && parsed.meta.interactionType) || "";
    } catch (e) {
      // State isn't MSAL library state (e.g. a non-msal callback) — leave it alone.
    }
  }

  const isBridgeInteraction =
    interactionType === "popup" || interactionType === "silent";

  if (hasResponse && isMsalState && isBridgeInteraction) {
    broadcastResponseToMainFrame().catch((e) => {
      console.error("MSAL redirect bridge broadcast failed", e);
    });
  }
} catch (e) {
  console.error("MSAL redirect bridge init failed", e);
}
